import React, { useState } from 'react';

import { DeleteOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { GetItemStockInheritanceDTO } from '@recurrency/core-api-schema/dist/items/getItemStockInheritance';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { Empty, notification } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';

import { SearchSelect } from 'components/AsyncSelect/SearchSelect';
import { Button } from 'components/Button';
import { FlexSpace } from 'components/FlexSpace';
import { Table } from 'components/Table';
import { Tooltip } from 'components/Tooltip';

import { coreApiFetch } from 'utils/api';
import { truthy } from 'utils/boolean';
import { captureAndShowError } from 'utils/error';
import { splitIdNameStr } from 'utils/formatting';
import { asKeyOf } from 'utils/tables';

import { ItemStockInheritanceData } from './sidePane/sidePane.utils';
import { StockInheritanceDescendantBanner } from './sidePane/StockInheritanceDescendantBanner';

interface StockInheritanceTabContentProps {
  descendantItemId: string;
  descendantItemCode: string;
  itemStockInheritanceData: ItemStockInheritanceData;
}

interface ItemOption {
  value?: string | undefined;
  uid: string;
}

export const StockInheritanceTabContent = ({
  descendantItemId,
  descendantItemCode,
  itemStockInheritanceData,
}: StockInheritanceTabContentProps) => {
  const {
    isLoading,
    error: fetchError,
    validDescendants,
    allAncestorData,
    reloadInheritanceData,
  } = itemStockInheritanceData;

  // an errored fetch leaves allAncestorData empty, so saving would wipe the item's real ancestors
  const isEditDisabled = isLoading || !!fetchError;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const mapAncestorsToItems = (items: GetItemStockInheritanceDTO[]): ItemOption[] =>
    items.map((item) => ({
      uid: item.itemId,
      value: `${item.itemCode}: ${item.itemName}`,
    }));
  const [selectedItems, setSelectedItems] = useState<ItemOption[]>(mapAncestorsToItems(allAncestorData.items));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const filteredItems = selectedItems.filter(
        (item) =>
          item.uid.trim() !== '' && // Filter empty rows
          item.uid !== descendantItemId, // Filter out the descendant item itself
      );

      await coreApiFetch(schemas.items.updateItemStockInheritanceAncestors, {
        pathParams: { itemUid: descendantItemId },
        bodyParams: {
          ancestorIds: filteredItems.map((item) => item.uid),
        },
      });
      notification.success({
        message: 'Stock Inheritance Updated',
        description: `Stock inheritance set for item ${descendantItemCode}`,
      });

      // Fetch fresh data and rebuild selectedItems from server response
      // TODO should not fetch again, should handle in reloadInheritanceData
      const { data: freshData } = await coreApiFetch(schemas.items.getItemStockInheritance, {
        pathParams: { itemUid: descendantItemId },
      });
      const freshAncestors = freshData.items.filter((item) => item.type === 'ancestor');
      setSelectedItems(mapAncestorsToItems(freshAncestors));
      reloadInheritanceData();
    } catch (error) {
      captureAndShowError(error, 'Error updating stock inheritance');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnType<ItemOption>[] = [
    {
      title: 'Ancestor Item',
      dataIndex: asKeyOf<ItemOption>('value'),
      render: (value: string | undefined, _row: ItemOption, index: number) => (
        <SearchSelect
          size="small"
          className={css`
            width: 100%;
          `}
          searchParams={{
            searchIndex: SearchIndexName.Items,
          }}
          entityPlural="items"
          placeholder="Search items"
          value={value || undefined}
          onSelect={(newValue, option) => {
            const newSelectedItems = [...selectedItems];
            newSelectedItems[index] = {
              uid: option.uid,
              value: newValue,
            };
            setSelectedItems(newSelectedItems);
          }}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: asKeyOf<ItemOption>('value'),
      width: '200px',
      render: (value: string) => {
        if (!value) {
          return '-';
        }
        const selectedItemCode = splitIdNameStr(value).foreignId;
        const ancestorData = allAncestorData.items.filter((ancestor) => ancestor.itemCode === selectedItemCode)[0];
        return ancestorData ? (
          ancestorData.hasMismatchedBaseUoms ? (
            <FlexSpace alignItems="center" gap={0}>
              Incompatible UOMs{' '}
              <Tooltip title="Cannot inherit stock from this item due to incompatible units of measure.">
                <WarningOutlined
                  className={css`
                    margin-left: 4px;
                    color: ${colors.danger[500]};
                  `}
                />
              </Tooltip>
            </FlexSpace>
          ) : (
            'Saved'
          )
        ) : (
          'Unsaved'
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: asKeyOf<ItemOption>('value'),
      width: '80px',
      render: (_value: string, _row: ItemOption, index: number) => (
        <Button
          danger
          size="small"
          onClick={() => {
            const newSelectedItems = [...selectedItems];
            newSelectedItems.splice(index, 1);
            setSelectedItems(newSelectedItems);
          }}
        >
          <Tooltip title="Stop including this item as an ancestor on the next stock update." placement="left">
            <DeleteOutlined />
          </Tooltip>
        </Button>
      ),
    },
  ].filter(truthy);

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        gap: 20px;
      `}
    >
      <div>
        The total stock of the item at this location will be the stock for this item and the combined stock of its
        ancestors. Demand Planning changes will apply overnight.
      </div>
      <div>
        If this item already has a descendant, added ancestors will be merged into the descendant and shown on the
        descendant's page.
      </div>

      <StockInheritanceDescendantBanner validDescendants={validDescendants} />

      <Table
        isLoading={isLoading}
        columns={columns}
        data={selectedItems}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              description="This item does not inherit stock from any ancestors."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      <FlexSpace
        justify="flex-end"
        className={css`
          margin-top: 12px;
        `}
      >
        <Button
          size="small"
          icon={<PlusOutlined />}
          disabled={isEditDisabled}
          onClick={() => setSelectedItems([...selectedItems, { uid: '', value: '' }])}
        >
          Add Ancestor Item
        </Button>
        <Button
          type="primary"
          size="small"
          permissionsRequired={[TenantUserPermission.ActionEditItemSupplierDetails]}
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isEditDisabled}
        >
          Save Changes
        </Button>
      </FlexSpace>
    </div>
  );
};
